import React, { useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import ProcessingJourney from "./ProcessingJourney";
import { Journey, journeyHeadline } from "../sessionJourney";

/** The whole journey as one percentage: the steps done, plus the active step's own share where it can tell. */
export function journeyPercent(journey: Journey): number {
  if (journey.finished || journey.steps.length === 0) return 100;
  const done = journey.steps.filter((s) => s.state === "done").length;
  const active = journey.steps.find((s) => s.state === "active");
  const share = active?.progress != null ? Math.min(1, Math.max(0, active.progress)) : 0;
  return Math.round(((done + share) * 100) / journey.steps.length);
}

/**
 * One progress tracker for an upload, from the moment it lands until every step is done (owner, 2026-10-28: "a more
 * standard ux so the user has better concept of whats happening"): a title, the step in hand in one line, the whole as a
 * bar, and every step done / in hand / to come. A free upload ends when its voice-over is recorded, a premium one when
 * its video exists; then the tracker is gone.
 */
export default function UploadProgressPanel({
  journey,
  slow = false,
  title,
  embedded = false,
  showDone = false,
  offer,
  children,
}: {
  journey: Journey;
  /** The active step has not moved for longer than it should (useJourneyStall). */
  slow?: boolean;
  /** Defaults to what is being made: the analysis, or the premium analysis. */
  title?: string;
  /** Inside another card (the processing card): no card or title of its own. */
  embedded?: boolean;
  /** The journey was in hand during this visit: once finished, say so plainly (owner, 2026-10-28: "its not clear if
   * its done or not") instead of just vanishing. */
  showDone?: boolean;
  /** A free upload's All done offers premium for this swing (owner, 2026-10-31: the upsell at "All done"). */
  offer?: { text: string; label: string; onClick: () => void };
  children?: React.ReactNode;
}) {
  const [closed, setClosed] = useState(false);
  if (journey.finished) {
    if (!showDone || closed) return null;
    return (
      <section className="card flex items-start gap-3 border-emerald-200 bg-emerald-50 p-4" aria-label="Progress" data-testid="upload-progress-done">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-emerald-900">All done</div>
          <div className="text-sm text-emerald-800">
            {journey.premium
              ? "Your analysis is ready. Your narrated coaching plays first in the player: Coaching."
              : "Your analysis is ready, with your spoken coaching."}
          </div>
          {offer && !journey.premium && (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="upload-progress-offer">
              <span className="text-sm text-slate-700">{offer.text}</span>
              <button type="button" className="btn btn-primary text-xs" onClick={offer.onClick}>
                {offer.label}
              </button>
            </div>
          )}
        </div>
        <button type="button" aria-label="Close" className="rounded p-1 text-emerald-700 hover:bg-emerald-100" onClick={() => setClosed(true)}>
          <X className="h-4 w-4" />
        </button>
      </section>
    );
  }
  const percent = journeyPercent(journey);
  return (
    <section
      className={(embedded ? "w-full max-w-md text-left" : "card p-5") + " space-y-3"}
      aria-label="Progress"
      data-testid="upload-progress"
    >
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-3">
          {/* Inside the processing card its own heading names it. */}
          {embedded ? <span /> : (
            <h2 className="text-base font-semibold text-slate-900" data-testid="upload-progress-title">
              {title ?? (journey.premium ? "Making your premium analysis" : "Making your analysis")}
            </h2>
          )}
          <span className="text-xs font-medium tabular-nums text-slate-500">{percent}%</span>
        </div>
        <div
          className="h-2 overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label="Overall progress"
        >
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${percent}%` }} />
        </div>
        <p className="text-sm text-slate-700" data-testid="upload-progress-headline">{journeyHeadline(journey)}</p>
      </div>
      <ProcessingJourney journey={journey} slow={slow} />
      <p className="text-xs text-slate-500">You can leave this page: it carries on, and your result will be here when it's ready.</p>
      {children}
    </section>
  );
}
